/**
 * Gender Preference Manager (Singleton)
 * Persists user's gender preference (male/female) in localStorage
 *
 * On product pages:
 * - The active state is determined by the product's gender (from title)
 * - Clicking the opposite gender redirects to the gender_counterpart product
 */

const GenderPreference = {
  STORAGE_KEY: 'genderPreference',
  VALID_GENDERS: ['male', 'female'],
  DEBUG: false, // Set to true for console logging

  // Singleton guard - prevent multiple initializations
  _initialized: false,

  /**
   * Debug logger - only logs if DEBUG is true
   */
  log(...args) {
    if (this.DEBUG) console.log('[GenderPreference]', ...args);
  },

  /**
   * Get current gender preference from localStorage
   * @returns {string|null} 'male', 'female', or null
   */
  get() {
    try {
      const value = localStorage.getItem(this.STORAGE_KEY) || null;
      this.log('get() =>', value);
      return value;
    } catch (e) {
      // localStorage might be disabled (private browsing, etc.)
      console.error('[GenderPreference] localStorage read error:', e);
      return null;
    }
  },

  /**
   * Set gender preference
   * @param {string} gender - 'male' or 'female'
   * @returns {boolean} Success status
   */
  set(gender) {
    // Validate input
    if (!this.VALID_GENDERS.includes(gender)) {
      console.error('[GenderPreference] Invalid gender value:', gender);
      return false;
    }

    try {
      this.log('set() =>', gender);
      localStorage.setItem(this.STORAGE_KEY, gender);
      this.dispatchChangeEvent(gender);
      return true;
    } catch (e) {
      console.error('[GenderPreference] localStorage write error:', e);
      return false;
    }
  },

  /**
   * Clear gender preference entirely
   * @returns {boolean} Success status
   */
  clear() {
    try {
      this.log('clear() - removing gender preference');
      localStorage.removeItem(this.STORAGE_KEY);
      this.dispatchChangeEvent(null);
      return true;
    } catch (e) {
      console.error('[GenderPreference] localStorage clear error:', e);
      return false;
    }
  },

  /**
   * Check if current preference is female
   * @returns {boolean}
   */
  isFemale() {
    return this.get() === 'female';
  },

  /**
   * Toggle between male and female
   * @returns {string} The new gender value
   */
  toggle() {
    const newGender = this.isFemale() ? 'male' : 'female';
    this.set(newGender);
    return newGender;
  },

  /**
   * Check if preference is set
   * @returns {boolean}
   */
  isSet() {
    try {
      return localStorage.getItem(this.STORAGE_KEY) !== null;
    } catch (e) {
      return false;
    }
  },

  /**
   * Dispatch custom event for other scripts to listen to
   * @param {string|null} gender - The new gender value
   */
  dispatchChangeEvent(gender) {
    document.dispatchEvent(new CustomEvent('genderPreferenceChanged', {
      detail: { gender }
    }));
  },

  /**
   * Initialize the gender preference system
   * Protected by singleton pattern - only runs once
   */
  init() {
    // Singleton guard - prevent multiple initializations
    if (this._initialized) {
      this.log('init() - already initialized, skipping');
      return;
    }
    this._initialized = true;

    this.log('init() - complete');
  }
};


/**
 * Gender Toggle Custom Element
 * Handles click events on the toggle buttons in the header
 *
 * On product pages:
 * - Active state is set via Liquid based on product title
 * - Clicking the opposite gender redirects to counterpart product
 *
 * On non-product pages:
 * - Updates localStorage preference (for future use)
 */
class GenderToggle extends HTMLElement {
  constructor() {
    super();
    this._boundClickHandler = this.handleClick.bind(this);
  }

  connectedCallback() {
    // Add click listener
    this.addEventListener('click', this._boundClickHandler);
  }

  disconnectedCallback() {
    // Clean up listener when element is removed
    this.removeEventListener('click', this._boundClickHandler);
  }

  /**
   * Check if this toggle is on a product page
   */
  isProductPage() {
    return this.hasAttribute('data-is-product-page');
  }

  /**
   * Get the current product's gender (set by Liquid)
   */
  getCurrentProductGender() {
    return this.getAttribute('data-current-gender');
  }

  /**
   * Get the counterpart product URL (set by Liquid from metafield)
   */
  getCounterpartUrl() {
    return this.getAttribute('data-counterpart-url');
  }

  /**
   * Handle click on toggle buttons
   */
  handleClick(e) {
    const target = e.target.closest('[data-gender]');
    if (!target) return;

    e.preventDefault();
    e.stopPropagation(); // Prevent event from bubbling to other toggle instances

    const clickedGender = target.dataset.gender;

    console.log('[GenderToggle] Click detected:', {
      clickedGender,
      isProductPage: this.isProductPage(),
      currentProductGender: this.getCurrentProductGender(),
      counterpartUrl: this.getCounterpartUrl()
    });

    if (this.isProductPage()) {
      // On product pages: redirect to counterpart if clicking opposite gender
      const currentProductGender = this.getCurrentProductGender();

      if (clickedGender !== currentProductGender) {
        // User clicked the opposite gender
        const counterpartUrl = this.getCounterpartUrl();

        if (counterpartUrl) {
          // Redirect to counterpart product
          console.log('[GenderToggle] Redirecting to counterpart:', counterpartUrl);
          GenderPreference.set(clickedGender);
          window.location.href = counterpartUrl;
          return;
        } else {
          // No counterpart available
          console.log('[GenderToggle] No counterpart product available for this product');
          GenderPreference.set(clickedGender);
        }
      } else {
        console.log('[GenderToggle] Clicked already active gender, ignoring');
      }
    } else {
      // On non-product pages: just update localStorage preference
      const currentGender = GenderPreference.get();

      if (currentGender !== clickedGender) {
        GenderPreference.log('Toggle clicked:', clickedGender);
        GenderPreference.set(clickedGender);

        // Update UI for all toggles on the page
        this.updateAllTogglesUI(clickedGender);
      }
    }

    // Reset processing flag after a delay
    setTimeout(() => {
      this._isProcessing = false;
    }, 300);
  }

  /**
   * Update UI state for all gender toggles on the page
   * Only used on non-product pages
   */
  updateAllTogglesUI(activeGender) {
    const toggles = document.querySelectorAll('[data-gender-toggle]');

    toggles.forEach(toggle => {
      // Skip product page toggles (they use Liquid-set classes)
      if (toggle.hasAttribute('data-is-product-page')) return;

      const maleBtn = toggle.querySelector('[data-gender="male"]');
      const femaleBtn = toggle.querySelector('[data-gender="female"]');

      if (maleBtn) {
        maleBtn.classList.toggle('is-active', activeGender === 'male');
        maleBtn.setAttribute('aria-pressed', activeGender === 'male');
      }
      if (femaleBtn) {
        femaleBtn.classList.toggle('is-active', activeGender === 'female');
        femaleBtn.setAttribute('aria-pressed', activeGender === 'female');
      }
    });
  }
}

// Register custom element (only if not already defined)
if (!customElements.get('gender-toggle')) {
  customElements.define('gender-toggle', GenderToggle);
}

// Initialize on DOM ready (singleton pattern ensures only one init)
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => GenderPreference.init());
} else {
  GenderPreference.init();
}

// Export for use in other scripts
window.GenderPreference = GenderPreference;
